import { useNavigate, useLocation } from 'react-router';

import { useNavigationStore } from '~/core/stores/navigation_store';

import { logger } from '../logger/logger';
import { AppRouteKeys, getRouteFromPath } from '../router/router_utils';
import { AppRoutes } from '../router/routes';

export const useNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentRoute, setRoute } = useNavigationStore();

  const goTo = (to: AppRouteKeys, replace: boolean = false) => {
    try {
      // Check if we're in a browser environment
      if (typeof window === 'undefined') {
        logger.warn('Attempted to use navigation in server environment');
        return false;
      }

      const path = location.pathname;
      const currentRouteFromPath = getRouteFromPath(path) as AppRouteKeys;

      // Use the route from path or fallback to the store value
      let fromRoute = currentRouteFromPath || currentRoute;

      if (!fromRoute) {
        logger.warn(`⚠️ [Navigation] Cannot determine current route from path ${path}`);
        fromRoute = 'UNKNOWN' as AppRouteKeys; // Use a fallback
      } else if (currentRouteFromPath !== currentRoute) {
        // If there's a mismatch, log it and update the store
        logger.info(
          `📍 [Navigation] Updating route in store: path=${currentRouteFromPath}, store=${currentRoute || 'null'}`
        );
      }

      // Always update navigation store with current route
      setRoute(fromRoute);

      sessionStorage.setItem('navigation_from', fromRoute);
      sessionStorage.setItem('navigation_to', to);

      const originalFetch = window.fetch;

      window.fetch = function (input: RequestInfo | URL, init?: RequestInit) {
        const newInit: RequestInit = init ? { ...init } : {};
        const headers = new Headers(newInit.headers || {});

        // Get navigation info from sessionStorage to ensure it persists across requests
        const navFrom = sessionStorage.getItem('navigation_from') || fromRoute;
        // We don't need to send the navTo in headers - the server can determine it from the URL path
        sessionStorage.setItem('navigation_to', to); // Still store it for debugging purposes

        // Set navigation headers for server-side validation
        headers.set('X-Client-Navigation', 'true');
        headers.set('X-Client-Nav-From', navFrom);
        // No need to set X-Client-Nav-To since we can derive the target route from the current path

        newInit.headers = headers;

        return originalFetch(input, newInit);
      };

      setTimeout(() => {
        window.fetch = originalFetch;
      }, 2000); // Delay restoration to allow Remix navigation to complete

      // Execute the navigation
      const targetPath = AppRoutes[to].path;

      // Update route in store
      setRoute(to);

      // Perform navigation
      if (replace) {
        logger.error(`🔄 [Navigation] Replacing route to ${to}`);
        navigate(targetPath, { replace: true });
      } else {
        logger.error(`🔄 [Navigation] Navigating to ${to}`);
        navigate(targetPath);
      }

      // Clear navigation state after successful navigation
      setTimeout(() => {
        logger.info(`🧹 [Navigation] Clearing navigation state after redirecting to ${to}`);
        if (sessionStorage.getItem('navigation_from') !== null) {
          sessionStorage.removeItem('navigation_from');
        }
      }, 200);

      return true;
    } catch (error) {
      return false;
    }
  };

  return { goTo };
};

/**
 * Helper function to determine the route key from a URL path
 */
export function getRouteKeyFromPath(path: string): AppRouteKeys | null {
  return getRouteFromPath(path) as AppRouteKeys | null;
}
